import React, { useState, useEffect } from 'react';
import {
  Box, VStack, Heading, Text, Select, Input, Button, FormControl, FormLabel,
  SimpleGrid, Alert, AlertIcon, Badge, Divider, HStack, Spinner, Flex, Card, CardBody,
  Checkbox, List, ListItem, ListIcon, useToast, Textarea, RadioGroup, Radio, Stack,
} from '@chakra-ui/react';
import { CheckCircleIcon, TimeIcon, PhoneIcon, InfoIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { obtenerTiposPublicoOP, obtenerRequisitosTipoOP, preRegistroOP } from '../../api/opTramiteApi';

const PortalPreRegistroOP: React.FC = () => {
  const navigate = useNavigate();
  const [paso, setPaso] = useState<'form' | 'exito'>('form');
  const [tipos, setTipos] = useState<any[]>([]);
  const [requisitos, setRequisitos] = useState<any[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [loadingReq, setLoadingReq] = useState(false);
  const [loading, setLoading] = useState(false);
  const [folioGenerado, setFolioGenerado] = useState('');
  const toast = useToast();

  const [form, setForm] = useState({
    idTipoTramite: '', tipoPersona: 'FISICA',
    nombreSolicitante: '', primerApellido: '', segundoApellido: '',
    razonSocial: '', representanteLegal: '', rfc: '', curp: '',
    telefono: '', celular: '', correo: '',
    calleSolicitante: '', numeroExteriorSol: '', coloniaSolicitante: '',
    municipioDomicilio: '', estadoDomicilio: 'Tlaxcala', codigoPostal: '',
    direccion: '', observaciones: '',
  });

  const set = (f: string, v: string) => setForm(prev => ({ ...prev, [f]: v }));

  useEffect(() => {
    obtenerTiposPublicoOP()
      .then(setTipos)
      .catch(() => toast({ title: 'Error al cargar tipos de trámite', status: 'error', duration: 3000 }))
      .finally(() => setLoadingTipos(false));
  }, []);

  useEffect(() => {
    if (!form.idTipoTramite) { setRequisitos([]); return; }
    setLoadingReq(true);
    obtenerRequisitosTipoOP(Number(form.idTipoTramite))
      .then(data => setRequisitos(data.requisitos || []))
      .catch(() => setRequisitos([]))
      .finally(() => setLoadingReq(false));
  }, [form.idTipoTramite]);

  const handleSubmit = async () => {
    if (!form.idTipoTramite) { toast({ title: 'Seleccione el tipo de trámite', status: 'warning', duration: 2000 }); return; }
    if (!form.telefono) { toast({ title: 'El teléfono es obligatorio', status: 'warning', duration: 2000 }); return; }
    if (form.tipoPersona === 'FISICA' && !form.nombreSolicitante) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 2000 }); return;
    }
    if (form.tipoPersona === 'MORAL' && !form.razonSocial) {
      toast({ title: 'La razón social es obligatoria', status: 'warning', duration: 2000 }); return;
    }
    setLoading(true);
    try {
      const res = await preRegistroOP(form);
      setFolioGenerado(res.folio);
      setPaso('exito');
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  if (paso === 'exito') {
    return (
      <Box minH="100vh" bg="gray.50" py={8} px={{ base: 4, md: 10 }}>
        <VStack spacing={6} maxW="600px" mx="auto">
          <Box textAlign="center">
            <CheckCircleIcon color="green.500" boxSize={16} mb={3} />
            <Heading size="lg" color="green.600">¡Pre-registro exitoso!</Heading>
            <Text color="gray.600" mt={2}>Su folio de seguimiento es:</Text>
            <Text fontFamily="mono" fontSize="2xl" fontWeight="bold" color="#5b182b" my={2}>{folioGenerado}</Text>
            <Text fontSize="sm" color="gray.500">Guarde este folio para consultar el estatus de su trámite.</Text>
          </Box>
          <Card w="full" shadow="md">
            <CardBody>
              <Heading size="sm" mb={3}>Próximos pasos</Heading>
              <List spacing={3}>
                <ListItem><ListIcon as={CheckCircleIcon} color="green.400" />Anote o capture su folio.</ListItem>
                <ListItem><ListIcon as={TimeIcon} color="blue.400" />Preséntese en las oficinas de Obras Públicas.</ListItem>
                <ListItem><ListIcon as={InfoIcon} color="orange.400" />Lleve todos los documentos requeridos.</ListItem>
                <ListItem><ListIcon as={PhoneIcon} color="gray.500" />Horario: Lun–Vie 8:00–15:00 hrs.</ListItem>
              </List>
              <Divider my={3} />
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Dirección de Obras Públicas · H. Ayuntamiento de Totolac · Tlaxcala
              </Text>
            </CardBody>
          </Card>
          <Button colorScheme="red" variant="outline" onClick={() => { setPaso('form'); setForm(f => ({ ...f, idTipoTramite: '' })); }}>
            Realizar otro pre-registro
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8} px={{ base: 4, md: 10 }}>
      <VStack spacing={6} maxW="800px" mx="auto">
        <Box w="full">
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500" onClick={() => navigate('/portal/obras')}>
            Portal Obras Públicas
          </Button>
        </Box>

        <Box textAlign="center">
          <Box bg="#5b182b" borderRadius="full" w={14} h={14} mx="auto" mb={3} display="flex" alignItems="center" justifyContent="center">
            <InfoIcon color="white" boxSize={6} />
          </Box>
          <Heading size="lg" color="#5b182b">Pre-registro de Trámite Presencial</Heading>
          <Text color="gray.500" mt={1}>H. Ayuntamiento de Totolac · Dirección de Obras Públicas</Text>
        </Box>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            El pre-registro agiliza su atención al llegar a las oficinas. Recibirá un folio de seguimiento.
            Debe presentarse físicamente con su documentación.
          </Text>
        </Alert>

        {/* Tipo de trámite */}
        <Card w="full" shadow="sm">
          <CardBody>
            <Heading size="sm" mb={4} color="#5b182b">1. Tipo de Trámite</Heading>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Seleccione el trámite</FormLabel>
              {loadingTipos ? <Spinner size="sm" /> : (
                <Select value={form.idTipoTramite} onChange={e => set('idTipoTramite', e.target.value)} placeholder="— Seleccione —">
                  {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </Select>
              )}
            </FormControl>

            {loadingReq && <Spinner size="sm" mt={3} />}

            {requisitos.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="bold" fontSize="sm" mb={2}>Documentos requeridos:</Text>
                <List spacing={1}>
                  {requisitos.map((r: any) => (
                    <ListItem key={r.id} fontSize="sm">
                      <ListIcon as={CheckCircleIcon} color={r.obligatorio ? 'red.400' : 'gray.400'} />
                      {r.nombreRequisito}{r.obligatorio ? <Badge colorScheme="red" ml={1} fontSize="xs">Obligatorio</Badge> : ''}
                      {r.descripcion && <Text fontSize="xs" color="gray.500" ml={6}>{r.descripcion}</Text>}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Datos del solicitante */}
        <Card w="full" shadow="sm">
          <CardBody>
            <Heading size="sm" mb={4} color="#5b182b">2. Datos del Solicitante</Heading>
            <FormControl mb={4}>
              <FormLabel fontSize="sm">Tipo de persona</FormLabel>
              <RadioGroup value={form.tipoPersona} onChange={v => set('tipoPersona', v)}>
                <Stack direction="row" spacing={6}>
                  <Radio value="FISICA">Persona Física</Radio>
                  <Radio value="MORAL">Persona Moral</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {form.tipoPersona === 'FISICA' ? (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nombre(s)</FormLabel>
                  <Input size="sm" value={form.nombreSolicitante} onChange={e => set('nombreSolicitante', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Primer apellido</FormLabel>
                  <Input size="sm" value={form.primerApellido} onChange={e => set('primerApellido', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Segundo apellido</FormLabel>
                  <Input size="sm" value={form.segundoApellido} onChange={e => set('segundoApellido', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">RFC</FormLabel>
                  <Input size="sm" value={form.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} maxLength={13} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">CURP</FormLabel>
                  <Input size="sm" value={form.curp} onChange={e => set('curp', e.target.value.toUpperCase())} maxLength={18} />
                </FormControl>
              </SimpleGrid>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Razón Social</FormLabel>
                  <Input size="sm" value={form.razonSocial} onChange={e => set('razonSocial', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Representante Legal</FormLabel>
                  <Input size="sm" value={form.representanteLegal} onChange={e => set('representanteLegal', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">RFC</FormLabel>
                  <Input size="sm" value={form.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} maxLength={13} />
                </FormControl>
              </SimpleGrid>
            )}

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={3}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Teléfono</FormLabel>
                <Input size="sm" type="tel" value={form.telefono} onChange={e => set('telefono', e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Celular</FormLabel>
                <Input size="sm" type="tel" value={form.celular} onChange={e => set('celular', e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Correo electrónico</FormLabel>
                <Input size="sm" type="email" value={form.correo} onChange={e => set('correo', e.target.value)} />
              </FormControl>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Observaciones */}
        <Card w="full" shadow="sm">
          <CardBody>
            <Heading size="sm" mb={4} color="#5b182b">3. Observaciones</Heading>
            <FormControl>
              <FormLabel fontSize="sm">Observaciones o información adicional</FormLabel>
              <Textarea size="sm" rows={3} value={form.observaciones} onChange={e => set('observaciones', e.target.value)}
                placeholder="Información adicional que desee comunicar..." />
            </FormControl>
          </CardBody>
        </Card>

        <Button colorScheme="red" size="lg" w="full" onClick={handleSubmit} isLoading={loading}
          isDisabled={!form.idTipoTramite || !form.telefono}>
          Realizar Pre-registro
        </Button>

        <Text fontSize="xs" color="gray.400" textAlign="center">
          Al enviar este formulario acepta que sus datos serán tratados conforme a la normatividad municipal vigente.
        </Text>
      </VStack>
    </Box>
  );
};

export default PortalPreRegistroOP;
